import { useState } from "react";
import { Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Loader2, Lock } from "lucide-react";

export default function AdminLogin() {
  const { user, login, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Resolve the post-login destination:
  //  1. honor ?next=<path> if the user was bounced here by ProtectedRoute
  //  2. otherwise drop the user on /wizard (Bundle) — admins can navigate to /admin from the header.
  const params = new URLSearchParams(location.search);
  const nextParam = params.get("next");
  const destination = nextParam && nextParam.startsWith("/") ? nextParam : "/wizard";

  if (ready && user) return <Navigate to={destination} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const r = await login(username, pwd);
    setBusy(false);
    if (r.ok) navigate(destination, { replace: true });
    else setErr(r.error || "Login failed");
  };

  return (
    <main className="min-h-[90vh] grid grid-cols-1 lg:grid-cols-2" data-testid="page-admin-login">
      <div className="hidden lg:block bg-[#0B132B] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 mix-blend-screen" style={{ background: "radial-gradient(circle at 30% 20%, #00E5FF, transparent 50%)" }} />
        <div className="absolute inset-0 p-16 flex flex-col justify-between">
          <div>
            <div className="tw-overline !text-[#00E5FF]">Admin Panel</div>
            <h1 className="mt-3 text-5xl font-extrabold tracking-tighter">
              Manage plans,<br/> devices &amp; promos.
            </h1>
            <p className="mt-4 text-slate-300 max-w-md">
              All updates are reflected on the public site instantly.
            </p>
          </div>
          <div className="mono text-xs text-slate-400 tracking-widest">// TWCALC-ADMIN v1</div>
        </div>
      </div>

      <div className="grid place-items-center p-8">
        <form onSubmit={submit} className="w-full max-w-md surface-card" data-testid="admin-login-form">
          <Lock className="h-6 w-6 text-[#008B99]" />
          <h2 className="mt-3 text-3xl font-extrabold text-[#0B132B] tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Admin access only.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="tw-overline">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-sharp mt-2"
                required
                autoComplete="username"
                data-testid="admin-login-username"
                placeholder="admin"
              />
            </div>
            <div>
              <label className="tw-overline">Password</label>
              <input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="input-sharp mt-2"
                required
                autoComplete="current-password"
                data-testid="admin-login-password"
              />
            </div>
            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={busy} className="btn-brand w-full justify-center" data-testid="admin-login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign in
            </button>

            {/* Self-service registration (v1.37+). New reps land on /register, pick their
                Master Agent, answer 3 security questions, and submit. An admin reviews and
                approves before they can sign in. */}
            <div className="pt-2 text-center text-sm text-slate-500">
              Don't have an account?{" "}
              <Link to="/register" className="font-semibold text-[#008B99] hover:underline" data-testid="admin-login-register-link">
                Create one
              </Link>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
}
